import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2, Pencil, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function Transactions() {
  const { API } = useContext(AuthContext);
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    entries: [{ account_code: '', account_name: '', debit: 0, credit: 0 }]
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [transResponse, accountsResponse] = await Promise.all([
        axios.get(`${API}/transactions`),
        axios.get(`${API}/chart-of-accounts`)
      ]);
      setTransactions(transResponse.data);
      setAccounts(accountsResponse.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const addEntry = () => {
    setFormData({
      ...formData,
      entries: [...formData.entries, { account_code: '', account_name: '', debit: 0, credit: 0 }]
    });
  };

  const removeEntry = (index) => {
    setFormData({
      ...formData,
      entries: formData.entries.filter((_, i) => i !== index)
    });
  };

  const updateEntry = (index, field, value) => {
    const newEntries = [...formData.entries];
    newEntries[index][field] = value;

    if (field === 'account_code') {
      const account = accounts.find(a => a.code === value);
      if (account) {
        newEntries[index].account_name = account.name;
      }
    }

    setFormData({ ...formData, entries: newEntries });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/transactions/${editingId}`, formData);
        toast.success('Transacción actualizada exitosamente');
      } else {
        await axios.post(`${API}/transactions`, formData);
        toast.success('Transacción creada exitosamente');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar transacción');
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      description: '',
      entries: [{ account_code: '', account_name: '', debit: 0, credit: 0 }]
    });
    setEditingId(null);
  };

  const handleEdit = (transaction) => {
    setFormData({
      date: transaction.date,
      description: transaction.description,
      entries: transaction.entries.map(e => ({
        account_code: e.account_code,
        account_name: e.account_name,
        debit: e.debit,
        credit: e.credit
      }))
    });
    setEditingId(transaction.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Estás seguro de eliminar esta transacción?')) return;
    try {
      await axios.delete(`${API}/transactions/${id}`);
      toast.success('Transacción eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar transacción');
    }
  };

  const getTotalDebit = () => formData.entries.reduce((sum, e) => sum + parseFloat(e.debit || 0), 0);
  const getTotalCredit = () => formData.entries.reduce((sum, e) => sum + parseFloat(e.credit || 0), 0);

  const handleApprove = async (transaction) => {
    // Client-side quick check
    const debit = transaction.entries.reduce((sum, e) => sum + (e.debit || 0), 0);
    const credit = transaction.entries.reduce((sum, e) => sum + (e.credit || 0), 0);

    if (Math.abs(debit - credit) > 0.01) {
      toast.error('La transacción no está balanceada (Débito ≠ Crédito)');
      return;
    }

    try {
      await axios.put(`${API}/transactions/${transaction.id}/approve`);
      toast.success('Transacción aprobada');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar transacción');
    }
  };

  if (loading) {
    return <div className="p-8">Cargando...</div>;
  }

  return (
    <div className="p-8 space-y-6" data-testid="transactions-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Transacciones Contables</h1>
          <p className="text-muted-foreground">Registra y gestiona tus asientos contables</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="create-transaction-button">
              <Plus className="mr-2 h-4 w-4" />
              Nueva Transacción
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Editar Transacción' : 'Crear Transacción'}</DialogTitle>
              <DialogDescription>{editingId ? 'Modifica los datos del asiento contable' : 'Ingresa los datos del asiento contable'}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha</Label>
                  <Input
                    type="date"
                    data-testid="transaction-date-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descripción</Label>
                  <Input
                    data-testid="transaction-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descripción del movimiento"
                    required
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label>Asientos</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addEntry} data-testid="add-entry-button">
                    <Plus className="mr-1 h-3 w-3" /> Agregar Línea
                  </Button>
                </div>

                {formData.entries.map((entry, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end p-3 border rounded">
                    <div className="col-span-4">
                      <Label className="text-xs">Cuenta</Label>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                        value={entry.account_code}
                        onChange={(e) => updateEntry(index, 'account_code', e.target.value)}
                        required
                      >
                        <option value="">Seleccionar cuenta</option>
                        {accounts.map(acc => (
                          <option key={acc.code} value={acc.code}>{acc.code} - {acc.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Débito</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={entry.debit}
                        onChange={(e) => updateEntry(index, 'debit', e.target.value)}
                      />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Crédito</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={entry.credit}
                        onChange={(e) => updateEntry(index, 'credit', e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      {formData.entries.length > 1 && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removeEntry(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}

                <div className="flex justify-between items-center p-3 bg-secondary rounded">
                  <span className="font-semibold">Totales:</span>
                  <div className="flex gap-8">
                    <span className="font-mono">Débito: ${getTotalDebit().toFixed(2)}</span>
                    <span className="font-mono">Crédito: ${getTotalCredit().toFixed(2)}</span>
                    <span className={`font-mono ${Math.abs(getTotalDebit() - getTotalCredit()) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                      Balance: ${(getTotalDebit() - getTotalCredit()).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <Button type="submit" data-testid="submit-transaction-button" className="w-full">
                {editingId ? 'Actualizar Transacción' : 'Crear Transacción'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Transacciones</CardTitle>
          <CardDescription>{transactions.length} registros</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="transactions-table">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Fecha</th>
                  <th className="text-left p-3">Descripción</th>
                  <th className="text-left p-3">Origen</th>
                  <th className="text-left p-3">Estado</th>
                  <th className="text-right p-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => (
                  <tr
                    key={transaction.id}
                    className="border-b hover:bg-secondary/50 transition-colors cursor-pointer"
                    onClick={() => setSelectedTransaction(transaction)}
                  >
                    <td className="p-3 font-mono text-sm">{transaction.date}</td>
                    <td className="p-3">{transaction.description}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${transaction.source === 'whatsapp' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                        {transaction.source === 'whatsapp' ? 'WhatsApp' : 'Manual'}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${transaction.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                        {transaction.status === 'approved' ? 'Aprobado' : 'Pendiente'}
                      </span>
                    </td>
                    <td className="p-3 text-right space-x-2">
                      {transaction.status !== 'approved' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-green-600 hover:text-green-800 hover:bg-green-100"
                          title="Aprobar"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApprove(transaction);
                          }}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(transaction);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(transaction.id);
                        }}
                        data-testid={`delete-transaction-${transaction.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Transaction Detail Dialog */}
      <Dialog open={!!selectedTransaction} onOpenChange={(open) => !open && setSelectedTransaction(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Detalle de Transacción</DialogTitle>
            <DialogDescription>Información del asiento contable</DialogDescription>
          </DialogHeader>

          {selectedTransaction && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-semibold block text-muted-foreground">Fecha:</span>
                  <span>{selectedTransaction.date}</span>
                </div>
                <div>
                  <span className="font-semibold block text-muted-foreground">Estado:</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${selectedTransaction.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {selectedTransaction.status === 'approved' ? 'Aprobado' : 'Pendiente'}
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="font-semibold block text-muted-foreground">Descripción:</span>
                  <span className="text-lg font-medium">{selectedTransaction.description}</span>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium">Código</th>
                      <th className="text-left p-3 font-medium">Cuenta</th>
                      <th className="text-right p-3 font-medium">Debe</th>
                      <th className="text-right p-3 font-medium">Haber</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTransaction.entries?.map((entry, idx) => (
                      <tr key={idx} className="border-b last:border-0 hover:bg-muted/20">
                        <td className="p-3 font-mono">{entry.account_code}</td>
                        <td className="p-3">{entry.account_name}</td>
                        <td className="p-3 text-right font-mono text-blue-600">
                          {entry.debit > 0 ? `$${entry.debit.toLocaleString('es-CL')}` : '-'}
                        </td>
                        <td className="p-3 text-right font-mono text-red-600">
                          {entry.credit > 0 ? `$${entry.credit.toLocaleString('es-CL')}` : '-'}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-muted font-medium">
                      <td className="p-3" colSpan={2}>TOTALES</td>
                      <td className="p-3 text-right text-blue-800">
                        ${selectedTransaction.entries?.reduce((sum, e) => sum + (e.debit || 0), 0).toLocaleString('es-CL')}
                      </td>
                      <td className="p-3 text-right text-red-800">
                        ${selectedTransaction.entries?.reduce((sum, e) => sum + (e.credit || 0), 0).toLocaleString('es-CL')}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}